import { useEffect, useRef, useState } from 'react';
import {
  clientesApi,
  type AtualizarClienteRequest,
  type ClienteResumo,
} from '@/features/clientes/api/clientesApi';
import {
  veiculosApi,
  type CadastrarVeiculoRequest,
  type VeiculoResumo,
  type VeiculoKmRegistro,
  type OrcamentoDoVeiculo,
} from '@/features/veiculos/api/veiculosApi';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { apenasDigitos, formatarDocumento, formatarTelefone } from '@/shared/format/mascaras';

type Modo =
  /** `nome` pré-preenche o campo (cliente digitado em outra tela, ainda sem cadastro). */
  | { tipo: 'novo'; nome?: string }
  | { tipo: 'editar'; cliente: ClienteResumo };

type Props = {
  modo: Modo;
  aoFechar: () => void;
  /** `clienteId` é o id gravado — no modo novo, o do cliente recém-criado. */
  aoSalvar: (dados: AtualizarClienteRequest, clienteId: string) => Promise<void>;
  /**
   * Abre já rolado na seção "Veículos" (vindo do contador da lista de
   * clientes, `?secao=veiculos`). Só tem efeito na edição.
   */
  focarVeiculos?: boolean;
  /**
   * Só os dados do cliente, sem a seção "Veículos" — para completar o
   * cadastro de dentro da O.S., onde o veículo já é tratado pela placa.
   */
  somenteDados?: boolean;
  /** Título do modal; por padrão "Novo cliente" / "Editar cliente". */
  titulo?: string;
};

type LinhaVeiculo = {
  uid: string;
  /** id do veículo no banco; null se ainda não persistido */
  idExistente: string | null;
  /** Veículo sem placa (frota interna, máquina, ainda não emplacado). */
  semPlaca: boolean;
  placa: string;
  marca: string;
  modelo: string;
  ano: string;
  motor: string;
  km: string;
  kmAtualPersistido: number | null;
  novoKm: string;
  /** true para veículos persistidos cujo evento ainda não foi confirmado pelo servidor. */
  syncPendente: boolean;
};

function novaLinha(): LinhaVeiculo {
  return {
    uid: crypto.randomUUID(),
    idExistente: null,
    semPlaca: false,
    placa: '',
    marca: '',
    modelo: '',
    ano: '',
    motor: '',
    km: '',
    kmAtualPersistido: null,
    novoKm: '',
    syncPendente: false,
  };
}

function fromExistente(v: VeiculoResumo): LinhaVeiculo {
  return {
    uid: crypto.randomUUID(),
    idExistente: v.id,
    semPlaca: v.placa === null,
    placa: v.placa ?? '',
    marca: v.marca,
    modelo: v.modelo,
    ano: v.ano != null ? String(v.ano) : '',
    motor: v.motor ?? '',
    km: v.kmAtual != null ? String(v.kmAtual) : '',
    kmAtualPersistido: v.kmAtual ?? null,
    novoKm: '',
    syncPendente: v.syncPendente,
  };
}

function apenasInteiro(v: string, max = 7): string {
  return v.replace(/\D/g, '').slice(0, max);
}

function formatarKm(km: number | string): string {
  const n = typeof km === 'number' ? km : Number(km.replace(/\D/g, ''));
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('pt-BR')} km`;
}

function normalizarPlaca(v: string) {
  return v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
}

export function ClienteFormModal({
  modo,
  aoFechar,
  aoSalvar,
  focarVeiculos = false,
  somenteDados = false,
  titulo,
}: Props) {
  const editando = modo.tipo === 'editar';
  const [nome, setNome] = useState(editando ? modo.cliente.nome : (modo.nome ?? ''));
  const [documento, setDocumento] = useState(
    editando ? formatarDocumento(modo.cliente.documento ?? '') : '',
  );
  const [email, setEmail] = useState(editando ? (modo.cliente.email ?? '') : '');
  const [telefone, setTelefone] = useState(
    editando ? formatarTelefone(modo.cliente.telefone ?? '') : '',
  );
  const [aniversario, setAniversario] = useState(
    editando ? (modo.cliente.aniversario ?? '') : '',
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [veiculos, setVeiculos] = useState<LinhaVeiculo[]>([novaLinha()]);
  const [removidos, setRemovidos] = useState<string[]>([]);
  const [carregandoVeiculos, setCarregandoVeiculos] = useState(
    modo.tipo === 'editar' && !somenteDados,
  );
  const [historicoVeiculoId, setHistoricoVeiculoId] = useState<string | null>(null);
  const secaoVeiculosRef = useRef<HTMLFieldSetElement>(null);
  const jaFocouVeiculos = useRef(false);

  // Rola até os veículos depois que a lista chega — antes disso a seção
  // ainda tem só a linha vazia e a rolagem pararia no lugar errado.
  useEffect(() => {
    if (!focarVeiculos || !editando || carregandoVeiculos || jaFocouVeiculos.current) return;
    const el = secaoVeiculosRef.current;
    if (!el) return;
    jaFocouVeiculos.current = true;
    el.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    el.focus({ preventScroll: true });
  }, [focarVeiculos, editando, carregandoVeiculos]);

  useEffect(() => {
    if (modo.tipo !== 'editar' || somenteDados) return;
    let cancelado = false;
    setCarregandoVeiculos(true);
    veiculosApi
      .listarPorCliente(modo.cliente.id)
      .then((vs) => {
        if (cancelado) return;
        setVeiculos(vs.length > 0 ? vs.map(fromExistente) : [novaLinha()]);
      })
      .catch(() => {
        if (!cancelado) setVeiculos([novaLinha()]);
      })
      .finally(() => {
        if (!cancelado) setCarregandoVeiculos(false);
      });
    return () => {
      cancelado = true;
    };
  }, [modo, somenteDados]);

  function atualizarLinha(uid: string, patch: Partial<LinhaVeiculo>) {
    setVeiculos((arr) => arr.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  }

  function adicionarLinha() {
    setVeiculos((arr) => [...arr, novaLinha()]);
  }

  function removerLinha(uid: string) {
    setVeiculos((arr) => {
      const alvo = arr.find((l) => l.uid === uid);
      if (alvo?.idExistente) {
        setRemovidos((r) => [...r, alvo.idExistente!]);
      }
      const novo = arr.filter((l) => l.uid !== uid);
      return novo.length === 0 ? [novaLinha()] : novo;
    });
  }

  function veiculoPreenchido(l: LinhaVeiculo): boolean {
    return (
      l.semPlaca ||
      l.placa.trim() !== '' ||
      l.marca.trim() !== '' ||
      l.modelo.trim() !== '' ||
      l.ano.trim() !== '' ||
      l.motor.trim() !== ''
    );
  }

  function validarVeiculoNovo(l: LinhaVeiculo): string | null {
    const placa = normalizarPlaca(l.placa);
    // Sem placa: o veículo é identificado por marca/modelo/ano, então esses
    // campos passam a ser a única âncora — mas o ano segue opcional, como no
    // resto do app. Motor é opcional em qualquer caso.
    if (!l.semPlaca && placa.length !== 7)
      return 'Placa deve ter 7 caracteres (ex.: ABC1D23).';
    if (!l.marca.trim() || !l.modelo.trim()) return 'Marca e modelo são obrigatórios.';
    if (l.ano.trim() !== '' || !l.semPlaca) {
      const anoNum = Number(l.ano);
      if (!Number.isInteger(anoNum) || anoNum < 1900 || anoNum > new Date().getFullYear() + 1)
        return 'Ano inválido.';
    }
    if (l.km !== '') {
      const kmNum = Number(l.km);
      if (!Number.isInteger(kmNum) || kmNum < 0 || kmNum > 9_999_999)
        return 'KM inválido (0 a 9.999.999).';
    }
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);

    const novosParaSalvar = veiculos.filter((l) => l.idExistente === null && veiculoPreenchido(l));
    for (const l of novosParaSalvar) {
      const msg = validarVeiculoNovo(l);
      if (msg) {
        setErro(`Veículo ${normalizarPlaca(l.placa) || '(sem placa)'}: ${msg}`);
        return;
      }
    }

    const placas = new Set<string>();
    for (const l of veiculos) {
      const p = normalizarPlaca(l.placa);
      if (p === '' || l.idExistente) continue;
      if (placas.has(p)) {
        setErro(`Placa ${p} aparece mais de uma vez.`);
        return;
      }
      placas.add(p);
    }

    setSalvando(true);
    try {
      const emailNormalizado = email.trim() === '' ? null : email;
      const telefoneDigitos = apenasDigitos(telefone);
      const telefoneNormalizado = telefoneDigitos === '' ? null : telefoneDigitos;
      const documentoDigitos = apenasDigitos(documento);
      const aniversarioNormalizado = aniversario.trim() === '' ? null : aniversario;

      let clienteId: string;
      if (editando) {
        clienteId = modo.cliente.id;
        await clientesApi.atualizar(clienteId, {
          nome,
          documento: documentoDigitos === '' ? null : documentoDigitos,
          email: emailNormalizado,
          telefone: telefoneNormalizado,
          aniversario: aniversarioNormalizado,
        });
      } else {
        const resp = await clientesApi.criar({
          nome,
          documento: documentoDigitos === '' ? null : documentoDigitos,
          email: emailNormalizado,
          telefone: telefoneNormalizado,
          aniversario: aniversarioNormalizado,
        });
        clienteId = resp.id;
      }

      for (const id of removidos) {
        await veiculosApi.excluir(id);
      }

      for (const l of novosParaSalvar) {
        const cmd: CadastrarVeiculoRequest = {
          clienteId,
          placa: l.semPlaca ? null : normalizarPlaca(l.placa),
          marca: l.marca.trim(),
          modelo: l.modelo.trim(),
          ano: l.ano.trim() === '' ? null : Number(l.ano),
          motor: l.motor.trim(),
          kmAtual: l.km !== '' ? Number(l.km) : null,
        };
        await veiculosApi.cadastrar(cmd);
      }

      for (const l of veiculos) {
        if (!l.idExistente || l.novoKm.trim() === '') continue;
        const kmNum = Number(l.novoKm);
        if (!Number.isInteger(kmNum) || kmNum < 0) continue;
        await veiculosApi.registrarKm(l.idExistente, {
          km: kmNum,
          observacao: 'Atualização via cadastro do cliente',
        });
      }

      await aoSalvar(
        {
          nome,
          email: emailNormalizado,
          telefone: telefoneNormalizado,
          aniversario: aniversarioNormalizado,
        },
        clienteId,
      );
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      titulo={titulo ?? (editando ? 'Editar cliente' : 'Novo cliente')}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={handleSubmit}>
        {erro && <div className="form-error">{erro}</div>}

        <Field rotulo="Nome" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              minLength={2}
              maxLength={200}
            />
          )}
        </Field>

        <Field
          rotulo="Documento"
          dica="Opcional. CPF (11 dígitos) ou CNPJ (14 dígitos)."
        >
          {(id) => (
            <input
              id={id}
              className="input"
              value={documento}
              onChange={(e) => setDocumento(formatarDocumento(e.target.value))}
              inputMode="numeric"
              maxLength={18}
            />
          )}
        </Field>

        <Field rotulo="E-mail" dica="Opcional.">
          {(id) => (
            <input
              id={id}
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field rotulo="Telefone" dica="Opcional. Apenas números, com DDD.">
          {(id) => (
            <input
              id={id}
              className="input"
              type="tel"
              value={telefone}
              onChange={(e) => setTelefone(formatarTelefone(e.target.value))}
              maxLength={16}
              inputMode="numeric"
            />
          )}
        </Field>

        <Field rotulo="Data de aniversário" dica="Opcional.">
          {(id) => (
            <input
              id={id}
              className="input"
              type="date"
              value={aniversario}
              onChange={(e) => setAniversario(e.target.value)}
            />
          )}
        </Field>

        {!somenteDados && (
          <fieldset
            className="fieldset"
            ref={secaoVeiculosRef}
            tabIndex={-1}
            aria-label="Veículos do cliente"
          >
            <legend className="fieldset__legend">Veículos</legend>

            {editando && carregandoVeiculos && <p className="hint">Carregando veículos…</p>}

            <div className="veiculos-lista">
              {veiculos.map((l) => {
                const eExistente = l.idExistente !== null;
                return (
                  <div key={l.uid} className="veiculo-linha">
                    <div className="veiculo-linha__cabecalho">
                      <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {eExistente
                          ? `Veículo ${l.placa || `${l.marca} ${l.modelo} ${l.ano}`.trim() || 'sem placa'}`
                          : 'Novo veículo'}
                        {eExistente && <BadgeSyncPendente pendente={l.syncPendente} />}
                      </strong>
                      <div className="row-actions">
                        {!eExistente && (
                          <label className="checkbox checkbox--compact">
                            <input
                              type="checkbox"
                              checked={l.semPlaca}
                              onChange={(e) =>
                                // Limpa a placa ao marcar pra não sobrar meia
                                // digitação escondida atrás do campo desabilitado.
                                atualizarLinha(l.uid, {
                                  semPlaca: e.target.checked,
                                  placa: e.target.checked ? '' : l.placa,
                                })
                              }
                            />
                            <span>Sem placa</span>
                          </label>
                        )}
                        {eExistente && (
                          <Button
                            type="button"
                            onClick={() => setHistoricoVeiculoId(l.idExistente)}
                            aria-label={`Ver histórico de KM de ${l.placa}`}
                          >
                            Histórico de KM
                          </Button>
                        )}
                        <Button
                          type="button"
                          className="btn--danger"
                          onClick={() => removerLinha(l.uid)}
                          aria-label="Remover veículo"
                        >
                          Remover
                        </Button>
                      </div>
                    </div>
                    <div className="veiculo-novo-grid">
                      <Field
                        rotulo="Placa"
                        obrigatorio={!eExistente && !l.semPlaca}
                        dica={
                          l.semPlaca
                            ? 'Identificado por marca, modelo e ano.'
                            : undefined
                        }
                      >
                        {(id) => (
                          <input
                            id={id}
                            className="input"
                            value={l.semPlaca ? '— sem placa —' : l.placa}
                            disabled={eExistente || l.semPlaca}
                            onChange={(e) =>
                              atualizarLinha(l.uid, { placa: normalizarPlaca(e.target.value) })
                            }
                            maxLength={7}
                            placeholder="ABC1D23"
                          />
                        )}
                      </Field>
                      <Field rotulo="Marca" obrigatorio={!eExistente}>
                        {(id) => (
                          <input
                            id={id}
                            className="input"
                            value={l.marca}
                            disabled={eExistente}
                            onChange={(e) => atualizarLinha(l.uid, { marca: e.target.value })}
                            maxLength={60}
                          />
                        )}
                      </Field>
                      <Field rotulo="Modelo" obrigatorio={!eExistente}>
                        {(id) => (
                          <input
                            id={id}
                            className="input"
                            value={l.modelo}
                            disabled={eExistente}
                            onChange={(e) => atualizarLinha(l.uid, { modelo: e.target.value })}
                            maxLength={80}
                          />
                        )}
                      </Field>
                      <Field
                        rotulo="Ano"
                        obrigatorio={!eExistente && !l.semPlaca}
                        dica={l.semPlaca ? 'Opcional. Entra na identificação.' : undefined}
                      >
                        {(id) => (
                          <input
                            id={id}
                            className="input"
                            value={l.ano}
                            disabled={eExistente}
                            onChange={(e) =>
                              atualizarLinha(l.uid, {
                                ano: e.target.value.replace(/\D/g, '').slice(0, 4),
                              })
                            }
                            inputMode="numeric"
                            placeholder={String(new Date().getFullYear())}
                          />
                        )}
                      </Field>
                      <Field rotulo="Motor" dica="Opcional.">
                        {(id) => (
                          <input
                            id={id}
                            className="input"
                            value={l.motor}
                            disabled={eExistente}
                            onChange={(e) => atualizarLinha(l.uid, { motor: e.target.value })}
                            maxLength={60}
                            placeholder="1.0, 2.0 Turbo…"
                          />
                        )}
                      </Field>
                      {!eExistente && (
                        <Field rotulo="KM atual" dica="Opcional. Inteiro em quilômetros.">
                          {(id) => (
                            <input
                              id={id}
                              className="input"
                              value={l.km}
                              onChange={(e) =>
                                atualizarLinha(l.uid, { km: apenasInteiro(e.target.value) })
                              }
                              inputMode="numeric"
                              placeholder="Ex.: 85000"
                            />
                          )}
                        </Field>
                      )}
                    </div>
                    {eExistente && (
                      <div className="km-bloco">
                        <div className="km-bloco__atual">
                          <span className="km-bloco__rotulo">KM atual</span>
                          <strong>
                            {l.kmAtualPersistido != null ? formatarKm(l.kmAtualPersistido) : '— sem registro —'}
                          </strong>
                        </div>
                        <Field rotulo="Registrar nova leitura" dica="Será salva no histórico do veículo.">
                          {(id) => (
                            <input
                              id={id}
                              className="input"
                              value={l.novoKm}
                              onChange={(e) =>
                                atualizarLinha(l.uid, { novoKm: apenasInteiro(e.target.value) })
                              }
                              inputMode="numeric"
                              placeholder={
                                l.kmAtualPersistido != null
                                  ? `Ex.: ${l.kmAtualPersistido + 1000}`
                                  : 'Ex.: 85000'
                              }
                            />
                          )}
                        </Field>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 12 }}>
              <Button type="button" onClick={adicionarLinha}>
                + Adicionar veículo
              </Button>
            </div>
          </fieldset>
        )}
      </form>

      {historicoVeiculoId && (
        <HistoricoKmModal
          veiculoId={historicoVeiculoId}
          aoFechar={() => setHistoricoVeiculoId(null)}
        />
      )}
    </Modal>
  );
}

type HistoricoKmModalProps = {
  veiculoId: string;
  aoFechar: () => void;
};

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function HistoricoKmModal({ veiculoId, aoFechar }: HistoricoKmModalProps) {
  const [historico, setHistorico] = useState<VeiculoKmRegistro[]>([]);
  const [orcamentos, setOrcamentos] = useState<OrcamentoDoVeiculo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);
    Promise.all([
      veiculosApi.historicoKm(veiculoId),
      veiculosApi.historicoOrcamentos(veiculoId),
    ])
      .then(([h, o]) => {
        if (!cancelado) {
          setHistorico(h);
          setOrcamentos(o);
        }
      })
      .catch((e: unknown) => {
        if (!cancelado) setErro(e instanceof Error ? e.message : 'Falha ao carregar histórico.');
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [veiculoId]);

  // Dono atual do veículo = cliente do orçamento mais recente (já vem ordenado
  // por data desc). Serve só para destacar visualmente quem é o atual.
  const donoAtualId = orcamentos[0]?.clienteId ?? null;

  return (
    <Modal
      aberto
      titulo="Histórico do veículo"
      onFechar={aoFechar}
      rodape={<Button onClick={aoFechar}>Fechar</Button>}
    >
      {carregando ? (
        <p className="hint">Carregando…</p>
      ) : erro ? (
        <p role="alert" className="alert alert--error">
          {erro}
        </p>
      ) : (
        <>
          <h4 style={{ margin: '0 0 8px' }}>Ordens de serviço do veículo</h4>
          {orcamentos.length === 0 ? (
            <p className="hint">Nenhuma ordem de serviço registrada para este veículo.</p>
          ) : (
            <div className="table-scroll" style={{ marginBottom: 20 }}>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Nº</th>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Status</th>
                    <th className="table__numeric">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orcamentos.map((o) => (
                    <tr key={o.id}>
                      <td>{o.numero}</td>
                      <td>{formatadorData.format(new Date(o.data))}</td>
                      <td>
                        {o.clienteNome ?? '—'}
                        {o.clienteId === donoAtualId && (
                          <span className="badge badge--ativo" style={{ marginLeft: 6 }}>
                            atual
                          </span>
                        )}
                      </td>
                      <td className="table__muted">{o.status}</td>
                      <td className="table__numeric">
                        {o.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 style={{ margin: '0 0 8px' }}>Quilometragem</h4>
          {historico.length === 0 ? (
            <p className="hint">Nenhum registro de KM para este veículo.</p>
          ) : (
            <div className="table-scroll">
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Data</th>
                <th className="table__numeric">KM</th>
                <th>Origem</th>
                <th>Observação</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((h, idx) => {
                const anterior = historico[idx + 1];
                const delta = anterior ? h.km - anterior.km : null;
                return (
                  <tr key={h.id}>
                    <td>{formatadorData.format(new Date(h.data))}</td>
                    <td className="table__numeric">
                      <strong>{h.km.toLocaleString('pt-BR')}</strong>
                      {delta != null && delta !== 0 && (
                        <span
                          className="table__muted"
                          style={{ marginLeft: 8, fontSize: 12 }}
                        >
                          {delta > 0 ? '+' : ''}
                          {delta.toLocaleString('pt-BR')}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${h.origem === 'Orcamento' ? 'badge--enviado' : h.origem === 'Cadastro' ? 'badge--ativo' : 'badge--neutro'}`}>
                        {h.origem === 'Orcamento' ? 'Ordem de serviço' : h.origem}
                      </span>
                    </td>
                    <td className="table__muted">{h.observacao ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
